import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import './TablePage.css';
import './SearchPage.css';
import { humanFileSize, getApiUrl, encodePath } from './fileUtils';
import { fileKind, kindLabel, FileIcon } from './fileTypes';

export interface SearchResult {
  category: string;
  name: string;
  path: string;
  isDir: boolean;
  size?: number;
}

export const SEARCH_DEBOUNCE_MS = 250;
export const RESULT_LIMIT = 50;

const categoryLabels: Record<string, string> = {
  video: '視頻',
  audio: '音頻',
  text: '文字',
  hidden: '出家師父專看'
};

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Wrap every occurrence of any search term in <mark>.
// split() with a capturing group puts the matched parts at the odd indexes.
function highlight(text: string, terms: string[]): React.ReactNode[] {
  const re = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'gi');
  return text.split(re).map((part, i) => (i % 2 === 1 ? <mark key={i}>{part}</mark> : part));
}

function parentOf(p: string): string {
  return p.split('/').slice(0, -1).join('/');
}

const SearchPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [query, setQuery] = useState(() => new URLSearchParams(location.search).get('q') || '');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [searched, setSearched] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');

  useEffect(() => { document.title = '普熏十方 - 搜索'; }, []);

  // Type-ahead: wait until typing pauses, then search. Typing again cancels the pending
  // timer and aborts the in-flight request, so an older, slower response can never
  // overwrite the results of a newer query.
  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      history.replace('/search');
      setResults([]);
      setTotal(0);
      setStatus('idle');
      return undefined;
    }
    setStatus('loading');
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      // keep ?q= in the URL so Back from a result returns to the same search
      history.replace(`/search?q=${encodeURIComponent(trimmed)}`);
      try {
        const params = new URLSearchParams({ q: trimmed, limit: String(RESULT_LIMIT) });
        const res = await fetch(`/api/search?${params.toString()}`, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setResults(data.results);
        setTotal(data.total);
        setSearched(trimmed);
        setStatus('done');
      } catch (e: any) {
        if (e.name === 'AbortError') return;
        setResults([]);
        setTotal(0);
        setStatus('error');
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, history]);

  // Folders open in the file table; files open their containing folder.
  const openResult = (r: SearchResult) => {
    const target = encodePath(r.isDir ? r.path : parentOf(r.path));
    history.push(`/table/${r.category}${target ? '/' + target : ''}`);
  };

  const terms = searched.split(/\s+/);

  let statusText = '';
  if (status === 'idle') statusText = '輸入文件名稱開始搜索';
  else if (status === 'loading') statusText = '搜索中...';
  else if (status === 'error') statusText = '搜索失敗，請重試';
  else if (total === 0) statusText = '沒有找到匹配的文件';
  else if (total > results.length) statusText = `共 ${total} 個結果，顯示前 ${results.length} 個，請輸入更多關鍵字`;
  else statusText = `共 ${total} 個結果`;

  return (
    <div className="table-page-responsive search-page">
      <h2 style={{ textAlign: 'center', color: 'var(--main-coffee)', fontWeight: 600, letterSpacing: '0.15em', marginBottom: '20px' }}>
        普熏十方 - 搜索
      </h2>
      <div className="search-box">
        <svg className="search-box-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" /></svg>
        <input
          type="search"
          className="search-box-input"
          placeholder="輸入文件名稱，例如：阿彌陀經"
          aria-label="搜索文件名稱"
          autoFocus
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        {query && (
          <button type="button" className="search-box-clear" aria-label="清除" onClick={() => setQuery('')}>✕</button>
        )}
      </div>
      <div className={`search-status${status === 'error' ? ' search-status--error' : ''}`} role="status">{statusText}</div>
      {results.length > 0 && (
        <div className="table-responsive-wrapper">
          <table className="responsive-table">
            <thead>
              <tr>
                <th>名稱</th>
                <th>位置</th>
                <th>大小</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {results.map(r => {
                const parent = parentOf(r.path);
                const kind = fileKind(r.name, r.isDir);
                return (
                  <tr
                    key={`${r.category}/${r.path}`}
                    className={r.isDir ? 'dir-row' : 'file-row'}
                    style={{ cursor: 'pointer' }}
                    onClick={() => openResult(r)}
                  >
                    <td data-label="名稱">
                      <span className="file-name-cell">
                        <FileIcon kind={kind} />
                        <span>{highlight(r.name, terms)}</span>
                        <span className={`file-type-badge file-type-badge--${kind}`}>{kindLabel(kind)}</span>
                      </span>
                    </td>
                    <td data-label="位置">
                      <span className="search-location-label">位置：</span>
                      {[categoryLabels[r.category] || r.category, ...parent.split('/').filter(Boolean)].join(' / ')}
                    </td>
                    <td data-label="大小">{r.size !== undefined ? humanFileSize(r.size) : ''}</td>
                    <td data-label="操作">
                      {!r.isDir && (
                        <span style={{ display: 'inline-flex', gap: 12, justifyContent: 'center', alignItems: 'center' }}>
                          <a
                            href={getApiUrl('/api/preview', { category: r.category, p: r.path })}
                            title="在線觀看"
                            className="search-action"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                          >
                            <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                          </a>
                          <a
                            href={getApiUrl('/api/download', { category: r.category, p: r.path })}
                            title="下載"
                            className="search-action"
                            onClick={e => e.stopPropagation()}
                            download
                          >
                            <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                          </a>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SearchPage;
